import WebSocket from 'ws';
import { CLI_WEBSOCKET_CLIENT_PING_MS, logCliWebSocketError } from './cli-ws-client.js';
import { createWsBridge } from './create-ws-bridge.js';
import { buildBridgeUrl } from './build-bridge-url.js';
import { scheduleMainBridgeReconnect } from './reconnect/index.js';
import { handleBridgeMessage } from '../routing/handle-bridge-message.js';
import { createMainBridgeAuthRefreshHandler } from './main-bridge-auth-refresh-handler.js';
import { createMainBridgeCloseHandler } from './main-bridge-ws-close-handler.js';
import { createMainBridgeOpenHandler } from './main-bridge-ws-open-handler.js';
import type { MainBridgeWsLifecycleParams } from './main-bridge-ws-lifecycle-types.js';

export type { MainBridgeTokenState, MainBridgeWsLifecycleParams } from './main-bridge-ws-lifecycle-types.js';

/**
 * Owns connect / open / close / auth-refresh for the primary bridge WebSocket.
 */
export function createMainBridgeWebSocketLifecycle(params: MainBridgeWsLifecycleParams): {
  connect: () => void;
} {
  const {
    state,
    apiUrl,
    workspaceId,
    logFn,
    messageDeps,
    tokens,
    bridgeHeartbeat,
  } = params;

  let connect: () => void;
  const handleOpen = createMainBridgeOpenHandler(params);
  const handleClose = createMainBridgeCloseHandler(params, () => connect());
  const handleAuthInvalid = createMainBridgeAuthRefreshHandler(params, () => connect());

  connect = (): void => {
    if (state.closedByUser) return;
    if (state.reconnectTimeout != null) {
      clearTimeout(state.reconnectTimeout);
      state.reconnectTimeout = null;
    }
    if (state.reconnectAttempt === 0) {
      try {
        logFn('Connecting to bridge service…');
      } catch {
        /* ignore */
      }
    }
    bridgeHeartbeat?.stop();
    const prev = state.currentWs as WebSocket | null;
    if (prev) {
      prev.removeAllListeners();
      // `close()` while CONNECTING emits `error`; keep a sink until the socket finishes closing.
      prev.once('error', () => {});
      prev.once('close', () => {
        prev.removeAllListeners();
      });
      try {
        prev.close();
      } catch {
        /* ignore */
      }
      state.currentWs = null;
    }
    try {
      const url = buildBridgeUrl(apiUrl, workspaceId, tokens.accessToken);
      state.currentWs = createWsBridge({
        url,
        clientPingIntervalMs: CLI_WEBSOCKET_CLIENT_PING_MS,
        isActiveSocket: (socket) => state.currentWs === socket,
        onCompactHeartbeatAck: (seq) => {
          messageDeps.onBridgeHeartbeatAck?.(seq);
        },
        onAuthInvalid: handleAuthInvalid,
        onOpen: handleOpen,
        onClose: handleClose,
        onError: (err) => {
          if (state.mainOutage.startedAt != null) {
            return;
          }
          try {
            logCliWebSocketError(logFn, '[Bridge service]', err);
          } catch {
            /* ignore */
          }
        },
        onMessage: (data, sourceWs) => {
          try {
            handleBridgeMessage(data, messageDeps, sourceWs);
          } catch {
            /* ignore */
          }
        },
      });
    } catch {
      if (!state.closedByUser && state.currentWs == null) {
        try {
          scheduleMainBridgeReconnect(state, connect, logFn);
        } catch {
          /* ignore */
        }
      }
    }
  };

  return { connect };
}
